<template>
  <div class="tf-panel" data-testid="tf-panel">
    <!-- ── loading ──────────────────────────────────────────────────────── -->
    <v-skeleton-loader v-if="loading" type="article" data-testid="tf-skeleton" />

    <!-- ── nothing selected yet ─────────────────────────────────────────── -->
    <div
      v-else-if="!turnSelected"
      class="text-medium-emphasis text-caption pa-2"
      data-testid="tf-empty"
    >เลือกเทิร์นเพื่อดูขั้นตอนที่เกิดขึ้นจริงในเทิร์นนั้น (สกัดคำตอบ → apply → เดินหาข้อถัดไป → คำนวณ → เซฟ)</div>

    <!-- ── 🔴 THE THREE MEANINGS OF "no flow" (see EMPTY_* below) ───────── -->
    <div
      v-else-if="!hasSteps"
      class="pa-2"
      data-testid="tf-empty"
    >
      <v-alert :type="emptyState.type" variant="tonal" density="compact">
        <div>{{ emptyState.text }}</div>
        <div v-if="emptyState.hint" class="text-caption mt-1">{{ emptyState.hint }}</div>
      </v-alert>
    </div>

    <!-- ── the flow itself ──────────────────────────────────────────────── -->
    <template v-else>
      <!-- A wire version this build does not know: render what is readable, never throw. -->
      <v-alert
        v-if="unknownVersion"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-2"
        data-testid="tf-unknown-version"
      >
        ก้อน flow นี้เป็นเวอร์ชัน {{ flow.version }} ซึ่งหน้าจอนี้ยังไม่รู้จัก — แสดงเท่าที่อ่านได้
      </v-alert>

      <v-alert
        v-if="isTruncated"
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-2"
        data-testid="tf-truncated"
      >
        flow ของเทิร์นนี้ถูกตัดให้พอดีเพดานขนาด — ทิ้งไป {{ droppedSteps }} ขั้น (เหลือ {{ steps.length }} ขั้น)
        <div class="text-caption mt-1">
          เลข <b>ลำดับที่กระโดด</b> (เช่น 40 → 461) คือรอยของขั้นที่หายไป — ที่เก็บไว้คือหัวและท้ายของเทิร์น ส่วนที่ถูกทิ้งคือช่วงกลาง
        </div>
      </v-alert>

      <!-- 🔴 A preview turn (`persist == false`) runs every step but writes nothing, and the
           two write paths disagree about it: `WriteAnswerAsync` sits under `if (persist)` so
           it emits NO `update.write` step at all, while the income-dialog path emits one with
           `persisted: false`. Both shapes read as "the value never got written" unless the
           reader is told the turn was a dry run. -->
      <div
        v-if="hasPreviewStep"
        class="text-caption text-medium-emphasis mb-2"
        data-testid="tf-preview-note"
      >
        เทิร์นนี้มีขั้นที่ไม่ได้เขียนลง DB (preview / dry-run) — บางขั้นที่ปกติจะเขียนจริงอาจไม่ปรากฏในรายการนี้เลย
      </div>

      <!-- ── filters (a turn has ~30-170 steps) ─────────────────────────── -->
      <div class="d-flex align-center flex-wrap ga-1 mb-2" data-testid="tf-filters">
        <v-chip
          size="x-small"
          :color="onlyProblems ? 'error' : undefined"
          :variant="onlyProblems ? 'flat' : 'outlined'"
          :title="'แสดงเฉพาะขั้นที่ถูกปฏิเสธ / ข้าม / ผิดพลาด (' + problemCount + ' ขั้น)'"
          data-testid="tf-filter-rejected"
          @click="onlyProblems = !onlyProblems"
        >เฉพาะที่ถูกปฏิเสธ/ข้าม ({{ problemCount }})</v-chip>

        <span class="tf-sep" />

        <v-chip
          v-for="p in phaseOptions"
          :key="'ph-' + p.value"
          size="x-small"
          :color="phaseSet.has(p.value) ? p.color : undefined"
          :variant="phaseSet.has(p.value) ? 'flat' : 'outlined'"
          :title="p.title"
          :data-testid="'tf-filter-phase-' + p.value"
          @click="togglePhase(p.value)"
        >{{ p.value }} ({{ p.count }})</v-chip>

        <v-btn
          v-if="onlyProblems || phases.length"
          size="x-small"
          variant="text"
          title="ล้างตัวกรองทั้งหมด"
          data-testid="tf-filter-clear"
          @click="clearFilters"
        >ล้างตัวกรอง</v-btn>

        <v-spacer />

        <!-- 🔴 A COUNT THAT NEVER MOVES WHEN FOLDING DOES. Folding hides rows, not steps —
             `visibleSteps` is what the filters produced, and saying otherwise would let a
             reader believe a step went away when it only got tucked under a header. -->
        <span class="text-caption text-medium-emphasis" data-testid="tf-count">
          {{ visibleSteps.length }}/{{ steps.length }} ขั้น
        </span>
      </div>

      <div class="d-flex align-center flex-wrap ga-1 mb-2">
        <v-chip
          size="x-small"
          :variant="foldSkips ? 'flat' : 'outlined'"
          :color="foldSkips ? 'blue-grey' : undefined"
          :title="foldSkips
            ? 'ตอนนี้ยุบขั้นที่ไม่ได้ทำอะไร (walk.skip / calc.skip) ที่ติดกันตั้งแต่ 3 ขั้นขึ้นไปให้เหลือบรรทัดเดียว — กดที่บรรทัดนั้นเพื่อกางดูทีละขั้น'
            : 'ตอนนี้แสดง walk.skip / calc.skip ทุกขั้นแยกบรรทัด'"
          data-testid="tf-fold-skips"
          @click="foldSkips = !foldSkips"
        >
          {{ foldSkips ? 'ยุบขั้นที่ข้ามไป' : 'แสดงขั้นที่ข้ามทุกบรรทัด' }}
          <template v-if="foldSkips && foldedCount"> ({{ foldedCount }})</template>
        </v-chip>

        <v-chip
          size="x-small"
          :variant="showLegend ? 'flat' : 'outlined'"
          :color="showLegend ? 'primary' : undefined"
          title="คำอธิบายว่าแต่ละ kind และแต่ละ op ในเทิร์นนี้หมายถึงอะไร"
          data-testid="tf-legend-toggle"
          @click="showLegend = !showLegend"
        >อธิบายศัพท์</v-chip>
      </div>

      <!-- ── glossary · only the vocabulary THIS turn used ──────────────── -->
      <div v-if="showLegend" class="tf-legend mb-2" data-testid="tf-legend">
        <div class="tf-sec-head text-caption font-weight-medium mt-0">ขั้นแต่ละชนิด (kind) ในเทิร์นนี้</div>
        <table class="tf-table">
          <tbody>
            <tr v-for="k in legendRows.kinds" :key="'lk-' + k.code">
              <td class="tf-mono tf-legend-code">{{ k.code }}</td>
              <td>{{ k.th || 'ชนิดขั้นที่หน้าจอนี้ยังไม่รู้จัก' }}</td>
            </tr>
          </tbody>
        </table>

        <template v-if="legendRows.ops.length">
          <div class="tf-sec-head text-caption font-weight-medium">operation / intent (op) ในเทิร์นนี้</div>
          <table class="tf-table">
            <tbody>
              <tr v-for="o in legendRows.ops" :key="'lo-' + o.code">
                <td class="tf-legend-code">
                  <v-chip
                    size="x-small"
                    :color="opMeta(o.code).color"
                    :variant="opMeta(o.code).variant"
                  >{{ opMeta(o.code).label }}</v-chip>
                </td>
                <td>{{ o.th || ('op ที่หน้าจอนี้ยังไม่รู้จัก: ' + o.code) }}</td>
              </tr>
            </tbody>
          </table>
        </template>
      </div>

      <!-- ── master-detail · md+ = 2 คอลัมน์ · xs/sm = บน-ล่าง ──────────── -->
      <div class="tf-split" :class="{ 'tf-split--stacked': stacked }">
        <!-- master -->
        <div class="tf-list" data-testid="tf-list">
          <div
            v-if="!visibleSteps.length"
            class="text-medium-emphasis text-caption pa-2"
            data-testid="tf-list-empty"
          >ไม่มีขั้นที่ตรงกับตัวกรองนี้</div>

          <template v-for="(r, i) in rows" :key="r.key">
            <!-- A gap in `seq` is EVIDENCE, not a rendering artefact — say so where it happens. -->
            <div
              v-if="gapBefore(i)"
              class="tf-gap text-caption"
              :title="'ขั้นที่ ' + gapBefore(i) + ' ไม่ได้อยู่ในก้อนนี้ (ถูกตัดทิ้ง หรือถูกตัวกรองซ่อนไว้)'"
              :data-testid="'tf-gap-' + rowFirstSeq(r)"
            >⋯ ข้าม {{ gapBefore(i) }} ขั้น</div>

            <!-- ── a RUN of consecutive walk.skip, folded into one row ────────
                 🔴 Folded, never dropped: every skipped item is still one step underneath,
                 with its own reason. A turn on a part-filled form steps over 20-40 questions
                 before it finds the one to ask, and a list where those outnumber the real
                 work 10:1 is a list nobody reads to the end. -->
            <div
              v-if="r.type === 'group'"
              class="tf-group"
              :class="{ 'tf-group--on': r.expanded, 'tf-group--pinned': r.pinned }"
              :role="r.pinned ? null : 'button'"
              :tabindex="r.pinned ? null : 0"
              :data-testid="'tf-skipgroup-' + rowFirstSeq(r)"
              :title="r.pinned
                ? 'กางค้างไว้เพราะขั้นที่เลือกอยู่อยู่ในกลุ่มนี้ — เลือกขั้นอื่นก่อนถึงจะยุบได้'
                : (r.expanded ? 'ยุบกลับเป็นบรรทัดเดียว' : 'กดเพื่อดูทีละข้อว่าเป็นข้อไหน เพราะอะไร')"
              @click="r.pinned || toggleGroup(rowFirstSeq(r))"
              @keydown.enter="r.pinned || toggleGroup(rowFirstSeq(r))"
              @keydown.space.prevent="r.pinned || toggleGroup(rowFirstSeq(r))"
            >
              <div class="d-flex align-center flex-wrap ga-1">
                <span class="tf-seq text-caption">{{ rowFirstSeq(r) }}–{{ rowLastSeq(r) }}</span>
                <v-icon
                  size="x-small"
                  :icon="r.pinned ? 'mdi-pin-outline' : (r.expanded ? 'mdi-menu-down' : 'mdi-menu-right')"
                />
                <v-chip
                  size="x-small"
                  :color="phaseMeta(r.steps[0].phase).color"
                  variant="tonal"
                  :title="phaseMeta(r.steps[0].phase).title"
                >{{ r.steps[0].phase || '—' }}</v-chip>
                <span class="tf-kind text-caption" :title="kindTitle(r.steps[0].kind)">{{ r.steps[0].kind }}</span>
                <span class="text-caption">{{ r.headTh }}</span>
              </div>
              <div class="tf-step-sub text-caption text-medium-emphasis">
                {{ groupSummary(r.steps) }}
              </div>
            </div>

            <!-- ── one step (standalone, or inside an expanded group) ───────── -->
            <template v-for="s in rowSteps(r)" :key="'s-' + s.seq">
              <div
                class="tf-step"
                :class="[
                  'tf-step--' + outcomeMeta(s.outcome).tone,
                  { 'tf-step--on': s.seq === selectedSeq, 'tf-step--in-group': r.type === 'group' },
                ]"
                role="button"
                tabindex="0"
                :data-testid="'tf-step-' + s.seq"
                @click="select(s.seq)"
                @keydown.enter="select(s.seq)"
                @keydown.space.prevent="select(s.seq)"
              >
                <div class="d-flex align-center flex-wrap ga-1">
                  <span class="tf-seq text-caption">{{ s.seq }}</span>

                  <v-chip
                    size="x-small"
                    :color="phaseMeta(s.phase).color"
                    variant="tonal"
                    :title="phaseMeta(s.phase).title"
                  >{{ s.phase || '—' }}</v-chip>

                  <!-- raw `kind` stays the headline (rule 4); the Thai reading is the tooltip
                       and gets a full line of its own in the detail pane. -->
                  <span class="tf-kind text-caption" :title="kindTitle(s.kind)">{{ s.kind || '—' }}</span>

                  <!-- 🔴 `op` used to be a bare lowercase word buried in the sub-line. It is the
                       single most-asked question of this tab ("set หรือ edit แล้วมันทำอะไร"),
                       so it is a chip with a Thai label of its own now. -->
                  <v-chip
                    v-if="s.op"
                    size="x-small"
                    :color="opMeta(s.op).color"
                    :variant="opMeta(s.op).variant"
                    :title="opMeta(s.op).title"
                    :data-testid="'tf-op-' + s.seq"
                  >{{ opMeta(s.op).label }}</v-chip>

                  <v-chip
                    size="x-small"
                    :color="outcomeMeta(s.outcome).color"
                    :variant="outcomeMeta(s.outcome).variant"
                    :prepend-icon="outcomeMeta(s.outcome).icon"
                    :title="outcomeMeta(s.outcome).title"
                  >{{ outcomeMeta(s.outcome).label }}</v-chip>

                  <v-chip
                    v-if="s.persisted === false"
                    size="x-small"
                    color="blue-grey"
                    variant="outlined"
                    :title="NOT_PERSISTED_TITLE"
                  >ไม่ได้เขียนลง DB</v-chip>
                </div>

                <div v-if="refLine(s)" class="tf-step-sub text-caption text-medium-emphasis">
                  {{ refLine(s) }}
                </div>

                <!-- 🔴 THE LINE THAT MAKES A SKIP READABLE. A row whose whole content was the
                     word "ข้าม" answered neither "ข้ามอะไร" nor "ทำไม" — both were sitting in
                     `reasonTh`, one click away, on a list where nobody clicks 30 grey rows. -->
                <div
                  v-if="whyOf(s)"
                  class="tf-step-why text-caption text-medium-emphasis"
                  :title="s.reasonTh"
                  :data-testid="'tf-why-' + s.seq"
                >
                  <span v-if="whyOf(s).code" class="tf-code-tag" :title="'รหัสเหตุผล: ' + whyOf(s).code">{{ whyOf(s).codeTh }}</span>
                  {{ whyOf(s).text }}
                </div>
              </div>
            </template>
          </template>
        </div>

        <!-- detail -->
        <div class="tf-detail" data-testid="tf-detail">
          <div
            v-if="!selectedStep"
            class="text-medium-emphasis text-caption pa-2"
          >เลือกขั้นทางซ้ายเพื่อดูรายละเอียด</div>

          <template v-else>
            <div class="d-flex align-center flex-wrap ga-1 mb-1">
              <span class="tf-seq text-caption">{{ selectedStep.seq }}</span>
              <v-chip
                size="x-small"
                :color="phaseMeta(selectedStep.phase).color"
                variant="tonal"
                :title="phaseMeta(selectedStep.phase).title"
              >{{ selectedStep.phase || '—' }}</v-chip>
              <span class="tf-kind text-caption">{{ selectedStep.kind || '—' }}</span>
              <v-chip
                v-if="selectedStep.op"
                size="x-small"
                :color="opMeta(selectedStep.op).color"
                :variant="opMeta(selectedStep.op).variant"
                :title="opMeta(selectedStep.op).title"
                data-testid="tf-detail-op"
              >{{ opMeta(selectedStep.op).label }}</v-chip>
              <v-chip
                size="x-small"
                :color="outcomeMeta(selectedStep.outcome).color"
                :variant="outcomeMeta(selectedStep.outcome).variant"
                :prepend-icon="outcomeMeta(selectedStep.outcome).icon"
                :title="outcomeMeta(selectedStep.outcome).title"
              >{{ outcomeMeta(selectedStep.outcome).label }}</v-chip>
              <v-chip
                v-if="selectedStep.persisted === false"
                size="x-small"
                color="blue-grey"
                variant="outlined"
                :title="NOT_PERSISTED_TITLE"
                data-testid="tf-detail-notpersisted"
              >ไม่ได้เขียนลง DB</v-chip>
              <v-chip
                v-if="selectedStep.durationMs != null"
                size="x-small"
                variant="outlined"
                title="เวลาที่ขั้นนี้ใช้"
              >{{ selectedStep.durationMs }} ms</v-chip>

              <v-spacer />

              <v-btn
                icon="mdi-content-copy"
                size="x-small"
                variant="text"
                title="คัดลอกขั้นนี้เป็น JSON"
                aria-label="คัดลอกขั้นนี้เป็น JSON"
                data-testid="tf-detail-copy"
                @click="onCopyStep"
              />
            </div>

            <!-- What this KIND of step does, before any of its particulars. -->
            <div
              v-if="kindTh(selectedStep.kind)"
              class="text-caption mb-1"
              data-testid="tf-detail-kindth"
            >{{ kindTh(selectedStep.kind) }}</div>

            <table class="tf-meta">
              <tbody>
                <tr v-for="m in metaRows" :key="'m-' + m.key">

                  <td class="tf-meta-key">{{ m.key }}</td>
                  <td class="tf-meta-val">{{ m.value }}</td>
                </tr>
              </tbody>
            </table>

            <div v-if="selectedStep.labelTh" class="tf-labelth" data-testid="tf-detail-label">
              {{ selectedStep.labelTh }}
            </div>
            <div
              v-else
              class="text-caption text-medium-emphasis mt-1"
              data-testid="tf-detail-nolabel"
            >ขั้นนี้ไม่มีบรรทัดใน trace (บันทึกเชิงโครงสร้างอย่างเดียว)</div>

            <v-alert
              v-if="selectedStep.reasonTh"
              :type="outcomeMeta(selectedStep.outcome).alert"
              variant="tonal"
              density="compact"
              class="mt-2"
              data-testid="tf-reason"
            >{{ selectedStep.reasonTh }}</v-alert>

            <!-- ── ก่อน → หลัง ────────────────────────────────────────── -->
            <div class="tf-sec-head text-caption font-weight-medium">
              ก่อน → หลัง ({{ changes.length }})
            </div>
            <div
              v-if="!changes.length"
              class="text-caption text-medium-emphasis pl-1"
              data-testid="tf-changes-empty"
            >ขั้นนี้ไม่ได้แตะแถวคำตอบ</div>
            <div v-else class="tf-scroll-x" data-testid="tf-changes">
              <table class="tf-table">
                <thead>
                  <tr>
                    <th>คอลัมน์</th>
                    <th>ก่อน</th>
                    <th>หลัง</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(c, ci) in changes"
                    :key="'c-' + ci"
                    :class="{ 'tf-changed': c.changed }"
                    :title="c.changed ? 'เปลี่ยนในขั้นนี้' : 'ค่าเท่าเดิม'"
                    :data-testid="'tf-change-' + (c.field || 'unknown')"
                  >
                    <td class="tf-col-field">
                      <div class="tf-mono">{{ c.field || '—' }}</div>
                      <div v-if="fieldTh(c.field)" class="tf-sub">{{ fieldTh(c.field) }}</div>
                    </td>
                    <td class="tf-col-val"><span :class="valueClass(c.before)">{{ showValue(c.before) }}</span></td>
                    <td class="tf-col-val"><span :class="valueClass(c.after)">{{ showValue(c.after) }}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- ── รายการย่อย ────────────────────────────────────────── -->
            <template v-if="items.length">
              <div class="tf-sec-head text-caption font-weight-medium">
                รายการย่อยใน SubItemsJson ({{ items.length }})
              </div>
              <div class="tf-scroll-x" data-testid="tf-items">
                <table class="tf-table">
                  <thead>
                    <tr>
                      <th>itemId</th>
                      <th>ทำอะไร</th>
                      <th>รายการ</th>
                      <th>ยอด ก่อน → หลัง</th>
                      <th>สถานะ ก่อน → หลัง</th>
                      <th>ยังขาด</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(it, ii) in items"
                      :key="'it-' + ii"
                      :class="{ 'tf-changed': it.op !== 'keep' }"
                      :data-testid="'tf-item-' + (it.itemId || ('idx-' + ii))"
                    >
                      <td class="tf-mono">{{ it.itemId || '—' }}</td>
                      <td>
                        <v-chip
                          size="x-small"
                          :color="itemOpMeta(it.op).color"
                          :variant="itemOpMeta(it.op).variant"
                          :title="itemOpMeta(it.op).title"
                        >{{ itemOpMeta(it.op).label }}</v-chip>
                      </td>
                      <td>
                        <div>{{ it.labelTh || '—' }}</div>
                        <div v-if="it.subTypeCode" class="tf-sub tf-mono">{{ it.subTypeCode }}</div>
                      </td>
                      <!-- 🔴 ยอดเป็นสตริงพร้อมใช้ ("200000 /MONTH") — ห้ามแปลงหน่วยที่ชั้นนี้ -->
                      <td class="tf-col-val">
                        <span :class="valueClass(it.amountBefore)">{{ showValue(it.amountBefore) }}</span>
                        <span class="tf-arrow"> → </span>
                        <span :class="valueClass(it.amountAfter)">{{ showValue(it.amountAfter) }}</span>
                      </td>
                      <td class="tf-col-val">
                        <span :class="valueClass(it.statusBefore)">{{ showValue(it.statusBefore) }}</span>
                        <span class="tf-arrow"> → </span>
                        <span :class="valueClass(it.statusAfter)">{{ showValue(it.statusAfter) }}</span>
                      </td>
                      <td>
                        <span v-if="(it.missing || []).length" class="tf-mono">{{ (it.missing || []).join(', ') }}</span>
                        <span v-else class="text-disabled">—</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>

            <!-- ── detailJson ────────────────────────────────────────── -->
            <template v-if="selectedStep.detailJson">
              <div class="tf-sec-head text-caption font-weight-medium d-flex align-center">
                <span>ข้อมูลเพิ่มเติมของขั้นนี้ (detailJson)</span>
                <v-spacer />
                <v-btn
                  icon="mdi-content-copy"
                  size="x-small"
                  variant="text"
                  title="คัดลอก detailJson"
                  aria-label="คัดลอก detailJson"
                  data-testid="tf-detail-json-copy"
                  @click="onCopyDetailJson"
                />
              </div>
              <v-alert
                v-if="detailJsonTooBig"
                type="info"
                density="compact"
                variant="tonal"
                class="mb-1"
                data-testid="tf-detail-json-large"
              >
                ก้อนนี้ใหญ่ ({{ detailJsonKb }} KB) — แสดงแบบดิบเพื่อไม่ให้หน้าค้าง
                <template #append>
                  <v-btn
                    size="x-small"
                    variant="text"
                    data-testid="tf-detail-json-showall"
                    @click="detailJsonShowAll = true"
                  >แสดงทั้งหมด</v-btn>
                </template>
              </v-alert>
              <pre class="tf-code" data-testid="tf-detail-json">{{ detailJsonText }}</pre>
            </template>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
// Turn Flow inspector of the Tax Playground V2 console AND the read-only Tax Log V2
// viewer — "the life of one turn", step by step: LLM extract → focusIntent dispatch →
// each apply operation with the BEFORE/AFTER of every column it touched → item-level
// changes inside `SubItemsJson` → the walker's pick/skip with its reason → CALCULATE →
// every point that wrote to the DB.
//
// 🔴 FIVE RULES THIS FILE EXISTS TO ENFORCE
//
// 1. DEPENDENCY-FREE. This panel is mounted by `TaxLogV2Detail.vue`, which must never
//    pull in `services/taxPlaygroundV2Api`, `utils/taxPlaygroundV2Store.js` or
//    `components/TaxPlaygroundChatPanelV2.vue` — not even transitively. So this file
//    imports NOTHING. Every helper it needs is declared right here. Do not "tidy" a
//    shared helper import back in.
//
// 2. `tf-` CLASS PREFIX, ALWAYS. `TaxLogV2Detail.vue` reaches into its child panels
//    with `.tpg-inspector :deep(.cf-table / .cf-label / .code-block / …)`. Re-using one
//    of those names here would silently inherit that page's column widths. `.tf-code` is
//    this file's OWN dark box for the same reason (scoped styles do not inherit).
//
// 3. NOTHING IS COMPUTED OR CONVERTED HERE. `amountBefore`/`amountAfter` arrive as
//    ready-to-read strings ("200000 /MONTH", "40000 /MONTH ×12") and are printed
//    verbatim. Annualising a MONTH figure in the presentation layer is the exact class
//    of bug this whole tab was built to catch — doing it here would hide it again.
//
// 4. AN UNKNOWN VALUE IS SHOWN RAW. Unknown `phase` / `kind` / `outcome` / `field` /
//    item `op` fall back to the raw string, never to a friendly-looking default. This is
//    a debugging screen: painting a value the FE does not understand as "business as
//    usual" is how a new backend state goes unnoticed. Colour is never the only signal
//    either — every chip carries a `title` (§Accessibility A1).
//
// 5. `flow == null` HAS THREE MEANINGS AND THEY ARE NOT INTERCHANGEABLE — see
//    `emptyState()`. "เทิร์นนี้ไม่ได้ทำอะไร" is a claim this panel is never allowed to
//    make on its own.

/** Wire version this build understands (`TaxTurnFlowV2Dto.WireVersion`). */
const KNOWN_WIRE_VERSION = 1;

/** Above this size a `detailJson` blob is shown raw — pretty-printing it freezes the tab. */
const JSON_BIG_BYTES = 200 * 1024;

/**
 * 🔴 THE ONLY EVIDENCE THAT A PLAYGROUND TURN'S FLOW WAS SHED (T9).
 *
 * When the row hits the 1 MB cap, `TaxPlaygroundV2BAL.RowSentinel()` overwrites
 * `FlowJson` with `{"__truncated":…}` — which the typed DTO cannot carry, so
 * `GetTurnAsync` hands the FE `flow: null`, indistinguishable from "this turn has no
 * flow". The one surviving witness is the Thai line the guard appends to `trace`:
 *
 *   • [playground] ตัดข้อมูล flow: 812345 ไบต์ เพราะรวมทั้งแถวเกินเพดาน 1048576 ไบต์
 *
 * 🔴 The match is ANCHORED to the guard's own line prefix (`• [playground] `) with the
 * `m` flag, because `trace` also carries UNTRUSTED text — the node processor echoes the
 * customer's typed answer into the same buffer (`• set {ref} → answered = {value}`), and
 * an unanchored search for "flow" let a crafted answer forge this banner. Same rule and
 * same shape as `TRUNCATION_TRACE_LINE` in `TaxPlaygroundV2.vue`; keep them in step.
 */
const FLOW_SHED_TRACE_LINE = /^\s*•\s*\[playground\]\s+ตัดข้อมูล\s+flow\s*:\s*(\d+)\s*ไบต์([^\n]*)$/m;

/** Phase → colour + hover text. An unknown phase keeps its raw code and a neutral colour. */
const PHASE_META = {
  EXTRACT: { color: 'indigo', title: 'EXTRACT — LLM สกัดคำตอบจากข้อความลูกค้า' },
  INTENT: { color: 'purple', title: 'INTENT — ตีความ focusIntent แล้วส่งต่อ (dispatch)' },
  UPDATE: { color: 'teal', title: 'UPDATE — เขียนค่าลงแถวคำตอบทีละ operation' },
  WALK: { color: 'blue', title: 'WALK — เดินหาข้อถัดไปที่ต้องถาม (ข้ามข้อไหน เพราะอะไร)' },
  CALC: { color: 'deep-orange', title: 'CALC — รันสูตรคำนวณของฟอร์ม' },
  SAVE: { color: 'green', title: 'SAVE — จุดที่เขียนลงฐานข้อมูล' },
  // การ์ดที่ลูกค้ากรอกเอง (SHORT_FORM) มีช่วงของตัวเอง เพราะเทิร์นที่กดปุ่มบนการ์ดไม่เดินผ่าน EXTRACT และไม่เดินผ่าน
  // ลูป UPDATE เลย — contract ถูกสังเคราะห์ขึ้นมาเปล่า ๆ แล้วสาขาการ์ดเขียนแถวเอง กรอง CARD = "การ์ดใบนี้ทำอะไรลงไป"
  // การ์ดสรุป (SUMMARY) ก็อยู่ช่วงนี้ด้วย: `card.publish` / `card.hold` — เทิร์นที่ส่งการ์ดสรุปอาจไม่ได้ walk เลย
  // (เช่น resume จังหวะ 2) จึงรายงานเป็น WALK ไม่ได้ · กรอง CARD = "การ์ดในเทิร์นนี้เกิดอะไรขึ้นบ้าง"
  CARD: { color: 'pink', title: 'CARD — การ์ดในเทิร์นนี้: การ์ดที่ลูกค้ากรอกเอง (ส่ง → postback → กระจายค่า) และการ์ดสรุปที่ส่ง/ถูกพักไว้' },
  // ตัวตรวจข้อความก่อนส่ง มีช่วงของตัวเอง เพราะมันไม่ได้อ่านหรือเขียนคำตอบเลยสักตัว — มันแก้ "ตัวหนังสือที่ลูกค้าจะเห็น"
  // หลังทุกอย่างตัดสินใจเสร็จแล้ว (อยู่เหนือ `turnLog.Flush` ⇒ สิ่งที่ลูกค้าเห็นกับ `Log_Message.replymsg` เป็น string
  // เดียวกันเสมอ) · กรอง GUARD = "เทิร์นนี้ข้อความขาออกถูกแก้ไปกี่จุด และแก้เพราะอะไร"
  GUARD: { color: 'brown', title: 'GUARD — ตรวจ/แก้ข้อความก่อนส่งถึงลูกค้า' },
};

/**
 * Outcome → chip. `tone` drives the row's left border, `alert` the `reasonTh` alert type.
 * 🔴 `applied` means "written to the answer row", NOT "committed to the DB" — that fact
 * lives in `persisted` and gets its own chip.
 */
const OUTCOME_META = {
  applied: {
    label: 'สำเร็จ', color: 'success', variant: 'flat', icon: 'mdi-check', tone: 'ok', alert: 'info',
    title: 'applied — ค่าถูกเขียนลงแถวคำตอบแล้ว (จะลง DB จริงหรือไม่ ดูป้าย “ไม่ได้เขียนลง DB”)',
  },
  rejected: {
    label: 'ถูกปฏิเสธ', color: 'error', variant: 'flat', icon: 'mdi-close', tone: 'bad', alert: 'error',
    title: 'rejected — operation นี้ถูกปฏิเสธ ค่าไม่ถูกเขียน (เหตุผลอยู่ในรายละเอียด)',
  },
  error: {
    label: 'ผิดพลาด', color: 'error', variant: 'flat', icon: 'mdi-alert', tone: 'bad', alert: 'error',
    title: 'error — ขั้นนี้ล้ม (โหนด fail-soft จึงเดินต่อ)',
  },
  noop: {
    label: 'ไม่มีผล', color: 'grey', variant: 'tonal', icon: 'mdi-minus', tone: 'dim', alert: 'warning',
    title: 'noop — ทำงานแล้วแต่ไม่มีอะไรเปลี่ยน',
  },
  skipped: {
    label: 'ข้าม', color: 'grey', variant: 'tonal', icon: 'mdi-debug-step-over', tone: 'dim', alert: 'warning',
    title: 'skipped — ขั้นนี้ถูกข้าม (เหตุผลอยู่ในรายละเอียด)',
  },
};

/** The outcomes that mean "something did not happen" — what the filter chip selects. */
const PROBLEM_OUTCOMES = ['rejected', 'error', 'skipped', 'noop'];

/** The outcomes worth landing on by default — a reader opens this tab because something broke. */
const ALARM_OUTCOMES = ['rejected', 'error'];

const NOT_PERSISTED_TITLE =
  'ขั้นนี้ไม่ได้เขียนลงฐานข้อมูล — เทิร์นเดินแบบ preview / dry-run (persist = false) '
  + 'logic เดินครบทุกขั้นแต่ DB ไม่ขยับ';

/**
 * 🔴 `changes[].field` IS THE `TaxFormAnswerV2` COLUMN NAME, deliberately — whoever reads
 * this screen is about to `SELECT` that column. So the raw name stays the headline and the
 * Thai reading is a subtitle; a field with no entry here shows the raw name ALONE rather
 * than a made-up label.
 */
const FIELD_TH = {
  RawAnswer: 'ข้อความดิบจากลูกค้า',
  NormalizedValue: 'ค่าที่ normalize แล้ว',
  AnswerValuesJson: 'ค่าหลายช่องของข้อนี้ (JSON)',
  AnswerStatus: 'สถานะคำตอบของข้อ',
  IsComplete: 'ตอบครบหรือยัง',
  Reason: 'เหตุผล/หมายเหตุของแถว',
  SubItemsJson: 'รายการย่อย (JSON)',
  MetaJson: 'ข้อมูลกำกับ (JSON)',
  CalcResultJson: 'ผลการคำนวณของฟอร์ม (JSON)',
  PendingQuestionItemID: 'ข้อที่ฟอร์มกำลังถามอยู่',
  PendingClarifyJson: 'คำถามที่ park ไว้ (JSON)',
  CurrentGroupCode: 'หมวดที่ฟอร์มอยู่ตอนนี้',
  PostCompleteEditStatus: 'โหมดแก้หลังจบ (null = ไม่เคย · Editing · Edited)',
  CurrentStateCode: 'หมุดฉาก (SAVE_STATE) ที่ฟอร์มยืนอยู่',
  CurrentStateGroup: 'กลุ่มสถานะของแถบแอป',
  Status: 'สถานะของฟอร์ม',
};

/**
 * `kind` → what that step actually DOES, in one Thai line.
 *
 * 🔴 A GLOSS, NEVER A REPLACEMENT. The raw `kind` stays the headline everywhere (rule 4) —
 * this is the tooltip on the list row and a full line in the detail pane. A kind with no
 * entry here simply has no gloss; it is never given a plausible-sounding invented one,
 * because a new backend step must LOOK new.
 */
const KIND_TH = {
  'llm.extract': 'เรียก LLM สกัดคำตอบจากข้อความของลูกค้า',
  'llm.extract.failed': 'เรียก LLM สกัดคำตอบแล้วไม่สำเร็จ (เทิร์นนี้จึงไม่มี contract ให้ทำงานต่อ)',
  'intent.normalize': 'แปลงคำ focusIntent ที่โมเดลส่งมา ให้เป็นคำศัพท์ที่ระบบดำเนินการต่อได้',
  'intent.fallback': 'ตัดสินว่าจะยกทั้งประโยคของลูกค้าไปเป็นคำตอบของข้อที่ถามอยู่ได้หรือไม่',
  'intent.dispatch': 'ส่ง intent ไปทำงานจริง (ตอบ / ข้าม / แจ้งว่าไม่มี / ถามกลับ)',
  'intent.restart': 'ประโยคที่วางไม่ลงกับคำถามบนจอ → ถาม LLM ว่าเป็นคำขอ «เริ่มแผนใหม่» ไหม (รับเฉพาะ NEW)',
  'intent.versionChoice': 'ถามสร้างแผนใหม่ / ใช้ข้อมูลเดิม (แผนอยู่บนเวอร์ชันเก่า)',
  'update.enter': 'รับ operation หนึ่งตัวเข้ามาในลูป — ยังไม่ได้ตัดสินว่าจะเขียนหรือไม่',
  'update.resolve': 'หาแถวคำตอบที่ operation นี้เล็งถึง',
  'update.reject': 'ปฏิเสธ operation นี้ ไม่มีอะไรถูกเขียนลงแถว',
  'update.lift': 'ย้าย operation ไปลงข้ออื่นที่ถูกต้องกว่าที่ LLM ระบุมา',
  'update.merge': 'รวมรายการย่อยเข้ากับบล็อกเดิมใน SubItemsJson (ไม่ทับทั้งก้อน)',
  'update.write': 'เขียนค่าลงแถวคำตอบ',
  'update.none': 'เทิร์นนี้ไม่มี operation ไหนลงแถวเลย',
  'classifier.skip': 'ไม่เขียนเวกเตอร์จำแนกประเภทเงินได้ในเทิร์นนี้',
  'walk.pass': 'เดินหาข้อถัดไปหนึ่งรอบ แล้วสรุปว่าไปหยุดที่ข้อไหน',
  'walk.skip': 'เดินผ่านข้อนี้ไปโดยไม่ถาม',
  'walk.ask': 'ข้อที่จะถูกถามลูกค้าจริงในเทิร์นนี้',
  // 🔴 Phase WALK ไม่ใช่ GUARD — prefix `guard.` ตรงนี้คือ loop-guard ของ walker ไม่ใช่ตัวตรวจข้อความขาออก (`guard.output`)
  'guard.count': 'loop-guard ของเทิร์นนี้: นับว่าติดค้างที่ข้อเดิมหรือไม่ (applied = นับ/ปลดถาวร · skipped = ไม่นับ เหตุผลอยู่ในบรรทัด) — ครบ 3 ครั้งข้อถูกข้ามถาวร',
  'calc.run': 'รันสูตรคำนวณ',
  'calc.skip': 'ไม่รันสูตรนี้ในเทิร์นนี้',
  'calc.write': 'เขียนผลคำนวณลงฟอร์ม (CalcResultJson)',
  'card.stale': 'พบว่าการ์ดสรุปที่เคยส่งไปแล้ว ตัวเลขไม่ตรงกับปัจจุบัน จึงเข้าคิวส่งใหม่',
  // ── การ์ดสรุป (SUMMARY): เดิมพูดผ่าน trace อย่างเดียว จนเทิร์นที่ส่งการ์ดไม่มีขั้นให้ดูเลย ──
  // 🔴 persisted บนสองขั้นนี้ = "เขียนแสตมป์ว่าแสดงแล้ว" ไม่ใช่ "ส่งการ์ดแล้ว" — การ์ดถึงลูกค้าได้โดยไม่แสตมป์
  //    (การ์ดของฉาก resume ที่ walk ยังไม่เคยเดินผ่าน ต้องกลับมาอีกครั้งคู่กับคำถามของมัน)
  'card.publish': 'ส่งการ์ดสรุปออกไปกับเทิร์นนี้ (ท้ายบรรทัดบอกว่าแสตมป์ว่าแสดงแล้วหรือไม่)',
  'card.hold': 'ไม่ส่งการ์ดสรุปในเทิร์นนี้ — เหตุผลอยู่ในบรรทัดเดียวกัน',
  // 🔴 persisted บนขั้นนี้เป็น false เสมอ — recall แค่เข้าคิว ไม่แสตมป์ที่ขั้นนี้ (แสตมป์อยู่ที่ `card.publish` ถัดไป)
  'card.recall': 'ลูกค้าพิมพ์คำเรียกการ์ดสรุป → ดึงการ์ดที่ walk เคยผ่านแล้วมาแนบกับคำถามที่ค้างจอ (applied = เข้าคิวส่ง · noop = ยังไม่เคยผ่าน/ประกอบไม่ได้ จึงไม่ดึง)',
  'gate.reopen': 'คำตอบในเทิร์นนี้ทำให้ข้อที่เคยถูกข้ามกลับมาถามได้อีกครั้ง',
  // ── การ์ดที่ลูกค้ากรอกเอง — เรียงตามลำดับที่มันเกิดจริง ────────────────────────────────────
  'card.render': 'ประกอบการ์ดแล้วส่งให้ลูกค้า (การ์ดคือคำถาม ไม่ได้เรียก LLM)',
  'card.submit': 'อ่านค่าที่ลูกค้ากดส่งกลับมาจากการ์ด (postback) — ยังไม่เขียนอะไรลงแถว',
  // ── ฟอร์มสั้น (SHORT_FORM): ค่าที่กรอกไปลงแถวคำตอบของข้ออื่นจริง ──
  'card.field': 'ผลของช่องหนึ่งช่องบนการ์ด: เขียนลงข้อพี่น้อง หรือไม่เขียนเพราะอะไร',
  'card.checkpoint': 'เขียนหมุดปิดการ์ด (บอกว่าจอนี้ตอบแล้ว — ไม่ใช่ยอดเงิน)',
  'card.fanout': 'สรุปทั้งใบว่าการ์ดกระจายค่าลงข้อพี่น้องไปกี่ข้อ',
  // ── หน้าจำลอง (SIMULATE): ตัวเลขเป็นแผนสมมติ ไม่เคยลงแถวคำตอบของข้อใด ──
  'card.slider': 'แถบเลื่อนหนึ่งแถบบนหน้าจำลอง — เก็บลงฉากจำลองเท่านั้น ไม่ใช่ค่าลดหย่อนที่ลูกค้ามีจริง',
  'card.scenario': 'สรุปฉากจำลองของเทิร์นนี้ (ขยับกี่แถบ ปิดหน้าจอหรือยัง)',
  'save.form': 'เขียนสถานะของฟอร์มลงฐานข้อมูล',
  'save.answer': 'เขียนแถวคำตอบลงฐานข้อมูล',
  // ── ตัวตรวจข้อความก่อนส่ง (GUARD) — ขั้นสุดท้ายของเทิร์น และเป็นขั้นเดียวที่เขียนทับ "ข้อความ" ไม่ใช่ "ค่า" ──
  // 🔴 outcome ที่นี่อ่านต่างจากขั้นอื่น: applied = ข้อความถูกแก้จริง · rejected = แก้แล้วถอนคืน (คืนของเดิม)
  //    เหตุผลอยู่ใน reasonTh · ขั้นนี้ไม่มี changes/items เพราะไม่มีแถวคำตอบไหนถูกแตะ
  'guard.output': 'ตรวจไวยากรณ์ข้อความก่อนส่งออก (แก้ token ที่โมเดลพิมพ์เพี้ยน เช่น `< brk/>` → `<brk/>`)',
  // เพิ่ม 2026-10-05 (TaxOutputLanguageCleanseV2) — รันก่อน guard.output · ขึ้นเฉพาะเทิร์นที่มีการตัด/แปลงจริง · applied = จำนวนอยู่ใน labelTh
  'guard.language': 'ตรวจภาษาข้อความก่อนส่งออก (เทิร์นไทย/อังกฤษ: ตัดอักษรจีนและแปลงวรรคตอน CJK → ASCII ในส่วนข้อความ — ไม่แตะการ์ด/ปุ่ม · rejected = ย้อนกลับ ส่งข้อความเดิม)',
  // ── หมวด «ขอแก้» (EDIT REQUEST) — เทิร์นพิเศษ Phase INTENT ที่ปรุงคำถามด้วย LLM ของหมวดนี้เอง เมื่อลูกค้าขอแก้
  // ข้อมูลตอนด่านยืนยันแต่ยังไม่บอกว่าจะแก้อะไร (T6/T7, 2026-09-03) · ไม่แตะแถวคำตอบเลย — Persisted เป็น false
  // เสมอ สิ่งเดียวที่เขียนคือ turn-state blob (มี save.form ของตัวเองรายงานแยกต่างหาก)
  'edit.ask': 'ถามลูกค้าว่าจะแก้รายการไหน — เปิดเมนูครั้งแรก, ถามซ้ำ, หรือหาไม่ได้เลยว่าจะเสนออะไรให้เลือก',
  'edit.resolve': 'ตัดสินจากคำตอบของลูกค้า: จับได้ว่าจะแก้ข้อไหน หรือลูกค้ายกเลิกขอแก้ หรือหมวดถูกปิดกลางทาง',
  'edit.giveup': 'ถามซ้ำจนครบโควตาแล้วยังจับไม่ได้ (หรือหารายการให้เลือกไม่เจอ) ⇒ ปิดเมนู กลับไปด่านยืนยันเดิม',
  // log อย่างเดียว (#50) — ออกเฉพาะเทิร์นที่ดูเป็นคำขอแก้ แต่ไม่มี step `edit.*` ตัวอื่นเลย · outcome noop เสมอ
  'edit.skip': 'ข้อความดูเป็นคำขอแก้ (confirm_edit / คำว่า แก้-เปลี่ยน-ปรับ / คำเรียกที่กรอกไว้) แต่ไม่มีทางขอแก้ไหนรับเทิร์นนี้ — เหตุผลบอกว่าแต่ละทาง (ค กลางฟอร์ม · ช แผนจบ · ฉ บนการ์ด · ข ด่านยืนยัน scope ว่าง) ตกเงื่อนไขไหน',
};

/**
 * `step.op` → chip. TWO vocabularies share this field on the wire and that is deliberate:
 * `set`/`edit`/`del` are the operations the LLM's contract asked for, while `answer`,
 * `skip`, `not_applicable`, … are the `focusIntent` the runtime dispatched on. They never
 * collide, and `title` always names the raw code so the reader can tell which is which.
 *
 * 🔴 An op with no entry keeps its raw code (rule 4) — including the ones a model invents.
 */
const STEP_OP_META = {
  // ── operation จาก contract ของ LLM ──
  set: { label: 'ตั้งค่า', color: 'teal', variant: 'flat', title: 'set — เขียนค่าใหม่ให้ข้อนี้ (ทับค่าเดิม)' },
  edit: { label: 'แก้รายการ', color: 'amber-darken-2', variant: 'flat', title: 'edit — แก้รายการย่อยที่มีอยู่แล้วในบล็อก โดยไม่ทับทั้งก้อน' },
  del: { label: 'ลบรายการ', color: 'error', variant: 'flat', title: 'del — เอารายการย่อยออกจากบล็อก' },
  // ── focusIntent ที่ระบบตีความจากข้อความลูกค้า ──
  answer: { label: 'ลูกค้าตอบ', color: 'success', variant: 'flat', title: 'answer — ข้อความนี้เป็นคำตอบของคำถามที่ถามอยู่' },
  partial_answer: { label: 'ตอบไม่ครบ', color: 'amber-darken-2', variant: 'flat', title: 'partial_answer — ตอบมาแล้วแต่ยังมีจุดที่ต้องถามต่อ (มี needsClarify ค้าง)' },
  question: { label: 'ลูกค้าถามกลับ', color: 'indigo', variant: 'flat', title: 'question — ข้อความเป็นคำถามของลูกค้า ไม่ใช่คำตอบ' },
  skip: { label: 'ขอข้ามข้อนี้', color: 'blue-grey', variant: 'flat', title: 'skip — ลูกค้าขอข้ามคำถามนี้ไปก่อน (ชั่วคราว · ค่าที่เก็บไว้แล้วยังอยู่ครบ)' },
  defer: { label: 'ขอตอบทีหลัง', color: 'blue-grey', variant: 'flat', title: 'defer — ลูกค้าขอไว้ตอบทีหลัง (ชั่วคราว เหมือน skip)' },
  no_data: { label: 'ยังไม่รู้ตัวเลข', color: 'blue-grey', variant: 'flat', title: 'no_data — ลูกค้ายังไม่รู้/ยังไม่มีตัวเลขจะให้' },
  not_applicable: { label: 'แจ้งว่าไม่มี', color: 'purple', variant: 'flat', title: 'not_applicable — ลูกค้าแจ้งว่าไม่มีรายการนี้ · ล้าง payload และตั้ง protected (ไม่ถูกเปิดใหม่)' },
  unclear: { label: 'ตีความไม่ได้', color: 'error', variant: 'flat', title: 'unclear — ระบบตีความข้อความไม่ได้ ⇒ ถามซ้ำ' },
  confirm_yes: { label: 'ยืนยันว่าถูก', color: 'success', variant: 'flat', title: 'confirm_yes — ลูกค้ายืนยันข้อมูลสรุปที่เห็น' },
  confirm_edit: { label: 'ขอแก้ข้อมูล', color: 'amber-darken-2', variant: 'flat', title: 'confirm_edit — ลูกค้าขอแก้ข้อมูลตอนยืนยัน' },
  // ── เทิร์นชนิดเดียวที่ walker เดินถอยหลัง ──
  reopen: { label: 'ย้อนกลับไปแก้', color: 'deep-orange', variant: 'flat', title: 'reopen — ลูกค้ากดปุ่มบนการ์ดเพื่อย้อนกลับไปแก้ข้อที่ปิดไปแล้ว · แถวถูกตั้งกลับเป็น PartialAnswer โดยค่าเดิมยังอยู่ครบ' },
};

/**
 * 🔴 THE `<code> — <Thai>` CONTRACT (backend: `TaxPlanningV2NodeProcessor.WalkSkipReasonTh`).
 *
 * A walk-skip reason leads with its machine code, then ` — `, then the explanation. Splitting
 * on that gives this panel both halves: the code drives the tally on a folded run of skips,
 * the Thai half is what the row shows. A reason that does NOT match is shown whole — the
 * regex is a bonus, never a requirement, so a reason from a step this build has not met yet
 * still reads correctly.
 */
const REASON_CODE_RE = /^([a-z][a-z0-9_]{2,31})\s+—\s+([\s\S]+)$/;

const REASON_CODE_TH = {
  gate_closed: 'เงื่อนไขไม่เข้า',
  not_askable: 'ถามไม่ได้',
  already_answered: 'ตอบไปแล้ว',
  // ── ช่องบนการ์ดฟอร์มสั้นที่ไม่ถูกเขียน (`card.field`) ──
  // 🔴 no_target คือรหัสเดียวในตารางนี้ที่แปลว่า "ยอดที่ลูกค้ากรอกหายไปจริง ๆ" — อีกสามตัวคือระบบทำถูกแล้ว
  not_on_card: 'ไม่ได้อยู่บนการ์ด',
  no_target: 'ไม่มีข้อรองรับ ⇒ ยอดหาย',
  // ── หมวด «ขอแก้» — backend ใช้จริง 15 รหัส (`TaxPlanningV2NodeProcessor.cs` EditAskTrigger*/Edit*Code) แต่ที่นี่
  // มีแค่ 12: `edit_resolved`/`edit_question`/`edit_reask` มากับ outcome=applied + labelTh เสมอ (ดู
  // `EnterEditRequestModeAsync`/`ResolveEditRequestTurnAsync`) ⇒ `whyOf()` (`:1178-1183`) ไม่พา reasonParts()
  // ไปอ่านโค้ดสามตัวนั้นเลยในตอนนี้ — ข้อความไทยเต็มยังอ่านได้จาก labelTh บนแถวและ reasonTh ในรายละเอียดอยู่แล้ว
  // ใส่ป้ายสั้นให้ก็ไม่มีที่แสดง จึงเว้นไว้ตามกติกาเดิมของตาราง (เติมเท่าที่มีประโยชน์จริง)
  edit_ask_confirm_unnamed: 'ขอแก้ไม่ระบุข้อ',
  edit_ask_scope_empty: 'สโคปว่างแต่ขอแก้',
  edit_ask_mid_form: 'ขอแก้กลางฟอร์ม',
  edit_ask_resume_update: 'ขอแก้หลัง resume',
  edit_ask_clarify_card: 'ขอแก้ขณะการ์ดค้าง',
  edit_ask_finished_plan: 'ขอแก้บนแผนที่จบแล้ว',
  edit_disabled: 'หมวดถูกปิดกลางทาง',
  edit_scope_lost: 'หารายการให้แก้ไม่เจอ',
  edit_giveup: 'ถามครบโควตาแล้ว',
  edit_cancelled: 'ยกเลิกขอแก้',
  edit_card_submit: 'กดส่งการ์ดแทนตอบเมนู',
  edit_value_handover: 'มีค่าใหม่ ส่งให้ท่อปกติเขียน',
  edit_skip: 'ดูเป็นคำขอแก้แต่ไม่มีทางรับ',
  // ── loop-guard ของ walker (`guard.count`, Phase WALK) — backend: `LoopGuardVerdictFor` · guard_counted/guard_retire
  // มากับ outcome=applied ⇒ ป้ายสั้นโผล่เฉพาะแถวที่ไม่มี ref/labelTh; guard_skip_* มากับ skipped ⇒ โผล่บนแถวเสมอ
  guard_counted: 'loop-guard นับเทิร์นนี้',
  guard_retire: 'loop-guard ครบกำหนด ⇒ ข้ามข้อนี้ถาวร',
  guard_skip_progressed: 'ไม่นับ — เทิร์นนี้คืบหน้า',
  guard_skip_flag: 'ไม่นับ — ทางที่รับเทิร์นตั้งธงไว้',
  guard_skip_question: 'ไม่นับ — ลูกค้าถามคำถาม',
  guard_skip_off_focus_write: 'ไม่นับ — ค่าลงแถวนอกข้อที่ถาม (#48)',
  guard_skip_edit_noop: 'ไม่นับ — คำขอแก้ที่ไม่มีทางรับ',
  // ── ไม่ใช่ตระกูล edit.* — มากับ `save.form`/`update.write` ตอนตัดรอบแก้หลังจบ (TaxPostCompleteEditCutV2.FlowReasonCode) ──
  post_complete_edit_cut: 'ตัดรอบแก้หลังจบ (session อื่น/กด Resume)',
};

/** A run of consecutive `walk.skip` this long or longer folds into one row. */
const SKIP_GROUP_MIN = 3;

/**
 * The kinds that fold, and the headline each folded run gets.
 *
 * 🔴 ONLY STEPS THAT DID NOTHING. Every entry here is a step the turn declined to take —
 * never one that wrote, resolved, or decided. A run of these is the ordinary state of a
 * part-filled form (a live income turn produced 28 `walk.skip` + 36 `calc.skip` out of 88
 * steps), so folding them is what makes the remaining ~24 steps — the ones that actually
 * moved something — readable at all.
 */
const GROUPABLE_KINDS = {
  'walk.skip': 'เดินผ่าน {n} ขั้นโดยไม่ถาม',
  'calc.skip': 'ไม่ได้รันสูตร {n} ขั้น',
};

/** Bucket label for a folded step whose reason carries no `<code> — ` prefix. */
const REASON_CODE_OTHER = 'ไม่ระบุรหัส';

/** Item-level op inside `SubItemsJson`. `keep` is information, not noise — it says merge SAW the row. */
const ITEM_OP_META = {
  add: { label: 'เพิ่ม', color: 'success', variant: 'flat', title: 'add — รายการนี้ไม่มีอยู่ก่อน merge' },
  edit: { label: 'แก้', color: 'amber-darken-2', variant: 'flat', title: 'edit — เทิร์นนี้เล็งรายการนี้ (ค่าอาจเท่าเดิมก็ได้ ซึ่งคือลายนิ้วมือของบั๊ก)' },
  del: { label: 'ลบ', color: 'error', variant: 'flat', title: 'del — มีอยู่ก่อน merge แต่ไม่เหลือหลัง merge' },
  keep: { label: 'คงเดิม', color: 'grey', variant: 'tonal', title: 'keep — รอดมาโดย merge ไม่ได้แตะ' },
  /* `miss` ไม่ใช่บรรทัดของบล็อก แต่เป็น "คำสั่งที่ยิงแล้วไม่โดน" — โมเดลอ้าง itemId ที่ไม่มีอยู่จริงในบล็อกนี้
     (มักเป็นเลขของอีกบล็อกหนึ่ง) merge จึงทิ้งคำสั่งนั้นทั้งอัน ยอดของแถวไม่ขยับ และถ้าไม่มีแถวนี้ หน้านี้จะดู
     เหมือน "โมเดลไม่ได้สั่งอะไรมา" ทั้งที่ลูกค้าสั่งแก้ไปแล้ว */
  miss: { label: 'ไม่พบรายการ', color: 'deep-orange', variant: 'flat', title: 'miss — โมเดลสั่งแก้/ลบ แต่หา itemId นี้ไม่เจอในบล็อก ⇒ ไม่มีอะไรถูกเขียน' },
};

export default {
  name: 'TaxTurnFlowPanelV2',
  props: {
    /** `TaxTurnFlowV2Dto` — `{ version, truncated, droppedSteps, steps[] }`, or null (see `emptyState`). */
    flow: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    /** false = ยังไม่ได้เลือกเทิร์น (คนละเรื่องกับ "เลือกแล้วแต่เทิร์นนั้นไม่มี flow"). */
    turnSelected: { type: Boolean, default: false },
    /** 'playground' | 'production' | '' — only used to gate the shed-detection below. */
    source: { type: String, default: '' },
    /** The turn's raw `trace`. The ONLY place a shed `flow` leaves a trace of itself. */
    traceText: { type: String, default: '' },
  },
  emits: ['copy'],
  data() {
    return {
      selectedSeq: null,
      onlyProblems: false,
      phases: [],
      detailJsonShowAll: false,
      /** First seq of each skip-run the reader has opened. */
      expandedGroups: [],
      /** false = show every walk.skip as its own row (the pre-folding behaviour). */
      foldSkips: true,
      showLegend: false,
      NOT_PERSISTED_TITLE,
    };
  },
  computed: {
    display() {
      return (this.$vuetify && this.$vuetify.display) || {};
    },
    /** xs/sm = the inspector column is far too narrow for two panes — stack them. */
    stacked() {
      return !this.display.mdAndUp;
    },
    steps() {
      return (this.flow && Array.isArray(this.flow.steps) && this.flow.steps) || [];
    },
    hasSteps() {
      return this.steps.length > 0;
    },
    isTruncated() {
      return Boolean(this.flow && this.flow.truncated);
    },
    droppedSteps() {
      return Number((this.flow && this.flow.droppedSteps) || 0);
    },
    /** Degrade, never throw (the DTO's own contract): render what is readable and say so. */
    unknownVersion() {
      const v = this.flow && this.flow.version;
      return v != null && Number(v) !== KNOWN_WIRE_VERSION;
    },
    /**
     * 🔴 `flow == null` / no steps means one of THREE different things, and "เทิร์นนี้ไม่ได้ทำอะไร"
     * is never one of them:
     *  1. the turn genuinely recorded no step;
     *  2. the turn predates this feature (there is no backfill);
     *  3. PLAYGROUND ONLY — the row hit its 1 MB cap and `flow` was shed. The typed DTO cannot
     *     carry the truncation sentinel, so it arrives as plain `null`; `trace` is the only
     *     witness left, which is why `flowWasShed` exists.
     * A fourth shape lives on the wire too: `flow != null` with `truncated: true` and an EMPTY
     * `steps[]` — "there were steps, they were too big to keep".
     */
    emptyState() {
      if (this.flowWasShed) {
        return {
          type: 'warning',
          text: 'flow ของเทิร์นนี้ถูกตัดทิ้งเพราะข้อมูลเทิร์นเกินขนาดที่เก็บได้',
          hint: 'ก้อน flow ชนเพดานขนาดตอนบันทึก ระบบจึงตัดทิ้ง (flow ถูกตัดเป็นตัวสุดท้ายเสมอ) — '
              + 'บรรทัดที่บอกว่าตัดไปกี่ไบต์อยู่ในแท็บ Trace',
        };
      }
      if (this.flow && this.isTruncated) {
        return {
          type: 'warning',
          text: 'เทิร์นนี้มีขั้นตอน แต่ใหญ่เกินกว่าที่จะเก็บไว้ทั้งหมด จึงไม่เหลือขั้นให้แสดง',
          hint: 'ถูกทิ้งไป ' + this.droppedSteps + ' ขั้น — ดูลำดับเหตุการณ์แบบข้อความได้ที่แท็บ Trace',
        };
      }
      if (!this.flow) {
        return {
          type: 'info',
          text: 'เทิร์นนี้ไม่มีข้อมูล Flow',
          hint: 'อาจเป็นเทิร์นที่บันทึกไว้ก่อนจะมีแท็บนี้ (ของเก่าไม่ถูกเติมย้อนหลัง) หรือเทิร์นนั้นไม่มีขั้นตอนที่บันทึกได้ — '
              + 'ลำดับเหตุการณ์แบบข้อความยังดูได้ที่แท็บ Trace',
        };
      }
      return {
        type: 'info',
        text: 'เทิร์นนี้ไม่มีขั้นตอนที่บันทึกไว้',
        hint: 'ก้อน flow มาถึงแล้วแต่ไม่มีขั้นอยู่ข้างใน — ดูแท็บ Trace ประกอบว่าโหนดทำอะไรไปบ้าง',
      };
    },
    /** true = `trace` says the guard cut this turn's `flow` (playground rows only). */
    flowWasShed() {
      // The guard that writes this line exists only on the playground persistence path;
      // a production trace can never contain it legitimately, so refuse to read one there.
      if (this.source === 'production') return false;
      return FLOW_SHED_TRACE_LINE.test(String(this.traceText || ''));
    },
    phaseSet() {
      return new Set(this.phases);
    },
    /** Only the phases this turn actually produced — an empty filter chip teaches nothing. */
    phaseOptions() {
      const order = [];
      const count = {};
      this.steps.forEach((s) => {
        const p = String((s && s.phase) || '—');
        if (count[p] == null) {
          count[p] = 0;
          order.push(p);
        }
        count[p] += 1;
      });
      return order.map((p) => ({
        value: p,
        count: count[p],
        color: this.phaseMeta(p).color,
        title: this.phaseMeta(p).title + ' · ' + count[p] + ' ขั้น',
      }));
    },
    /** Any step that ran without touching the DB ⇒ this turn was a preview / dry-run. */
    hasPreviewStep() {
      return this.steps.some((s) => s && s.persisted === false);
    },
    problemCount() {
      return this.steps.filter((s) => PROBLEM_OUTCOMES.indexOf(String((s && s.outcome) || '')) >= 0).length;
    },
    visibleSteps() {
      return this.steps.filter((s) => {
        if (this.onlyProblems && PROBLEM_OUTCOMES.indexOf(String((s && s.outcome) || '')) < 0) return false;
        if (this.phases.length && !this.phaseSet.has(String((s && s.phase) || '—'))) return false;
        return true;
      });
    },
    /**
     * The list as it is actually drawn: standalone steps, plus folded runs of `walk.skip`.
     *
     * 🔴 FOLDING IS PRESENTATION ONLY. `steps`, `visibleSteps`, `problemCount`, the phase
     * counts and the "n/m ขั้น" readout all keep counting individual steps, and every folded
     * step is still reachable by opening its group. Nothing here may make a step unreachable —
     * a skip that cannot be opened is a skip that was never recorded, as far as the reader is
     * concerned, and that is the failure this whole tab exists to prevent.
     *
     * The run containing the SELECTED step always renders open, so a selection can never point
     * at a row the list is not showing.
     */
    rows() {
      const out = [];
      let run = [];
      let runKind = null;
      const flush = () => {
        if (!run.length) return;
        const first = run[0].seq;
        const holdsSelection = run.some((s) => s.seq === this.selectedSeq);
        if (run.length >= SKIP_GROUP_MIN) {
          out.push({
            type: 'group',
            key: 'g-' + first,
            steps: run,
            // 🔴 From the RUN's kind, not a fixed string: a run of `calc.skip` headed
            // "เดินผ่าน N ขั้นโดยไม่ถาม" would describe a walk that never happened.
            headTh: String(GROUPABLE_KINDS[runKind] || '{n} ขั้น').replace('{n}', run.length),
            // `pinned` = held open by the selection, not by the reader. The header says so and
            // stops offering a toggle that could not work: collapsing would leave the detail
            // pane describing a row the list no longer shows.
            pinned: holdsSelection,
            expanded: holdsSelection || this.expandedGroups.indexOf(first) >= 0,
          });
        } else {
          run.forEach((s) => out.push({ type: 'step', key: 's-' + s.seq, steps: [s] }));
        }
        run = [];
        runKind = null;
      };

      this.visibleSteps.forEach((s) => {
        const kind = String((s && s.kind) || '');
        // Same kind only — a `walk.skip` and a `calc.skip` next to each other are two
        // different stories and must not end up under one header.
        if (this.foldSkips && Object.prototype.hasOwnProperty.call(GROUPABLE_KINDS, kind)) {
          if (runKind !== null && runKind !== kind) flush();
          runKind = kind;
          run.push(s);
          return;
        }
        flush();
        out.push({ type: 'step', key: 's-' + s.seq, steps: [s] });
      });
      flush();
      return out;
    },
    /** How many steps the folding is currently hiding behind a group header. */
    foldedCount() {
      return this.rows.reduce(
        (n, r) => n + (r.type === 'group' && !r.expanded ? r.steps.length : 0), 0);
    },
    /** Only the kinds and ops THIS turn produced — a legend of things not on screen teaches nothing. */
    legendRows() {
      const kinds = [];
      const ops = [];
      this.steps.forEach((s) => {
        const k = String((s && s.kind) || '');
        if (k && kinds.every((r) => r.code !== k)) kinds.push({ code: k, th: KIND_TH[k] || '' });
        const o = String((s && s.op) || '');
        if (o && ops.every((r) => r.code !== o)) {
          ops.push({ code: o, th: (STEP_OP_META[o] && STEP_OP_META[o].title) || '' });
        }
      });
      return { kinds, ops };
    },
    selectedStep() {
      if (this.selectedSeq == null) return null;
      return this.steps.find((s) => s && s.seq === this.selectedSeq) || null;
    },
    changes() {
      return (this.selectedStep && this.selectedStep.changes) || [];
    },
    items() {
      return (this.selectedStep && this.selectedStep.items) || [];
    },
    metaRows() {
      const s = this.selectedStep || {};
      const rows = [];
      if (s.ref) rows.push({ key: 'ref', value: s.ref });
      // Raw code first (it is what the wire carries), Thai reading appended — same rule as
      // `changes[].field`: the reader is about to go looking for this exact string.
      if (s.op) {
        const m = STEP_OP_META[String(s.op)];
        rows.push({ key: 'op', value: m ? s.op + ' — ' + m.title.replace(/^[a-z_]+ — /, '') : s.op });
      }
      if (s.questionItemId != null) rows.push({ key: 'questionItemId', value: String(s.questionItemId) });
      if (s.itemKind) rows.push({ key: 'itemKind', value: s.itemKind });
      return rows;
    },
    detailJsonText() {
      const raw = String((this.selectedStep && this.selectedStep.detailJson) || '');
      if (!raw) return '';
      if (this.detailJsonTooBig) return raw;
      try {
        return JSON.stringify(JSON.parse(raw), null, 2);
      } catch (e) {
        return raw;
      }
    },
    detailJsonKb() {
      return Math.round(String((this.selectedStep && this.selectedStep.detailJson) || '').length / 1024);
    },
    detailJsonTooBig() {
      const raw = String((this.selectedStep && this.selectedStep.detailJson) || '');
      return raw.length > JSON_BIG_BYTES && !this.detailJsonShowAll;
    },
  },
  watch: {
    flow: {
      immediate: true,
      handler() {
        this.onlyProblems = false;
        this.phases = [];
        this.detailJsonShowAll = false;
        // 🔴 `seq` restarts at 1 on every turn, so a group left open on the previous turn would
        // re-open an unrelated run here purely because the numbers collide.
        this.expandedGroups = [];
        this.foldSkips = true;
        this.showLegend = false;
        this.selectedSeq = this.defaultSeq();
      },
    },
    selectedSeq() {
      this.detailJsonShowAll = false;
    },
    visibleSteps() {
      // A filter that hides the selected step would leave the detail pane describing
      // something the list no longer shows — follow the filter instead.
      if (!this.visibleSteps.length) return;
      if (this.visibleSteps.some((s) => s && s.seq === this.selectedSeq)) return;
      this.selectedSeq = this.visibleSteps[0].seq;
    },
  },
  methods: {
    /**
     * 🔴 Default selection is the FIRST rejected/error step, not step 1.
     * Nobody opens this tab out of curiosity — they open it because a value went missing,
     * and the answer is almost always the first thing the turn refused to do. Landing on
     * `llm.extract` would make them scroll past 30 walk-skips to find it.
     */
    defaultSeq() {
      const alarm = this.steps.find((s) => s && ALARM_OUTCOMES.indexOf(String(s.outcome || '')) >= 0);
      if (alarm) return alarm.seq;
      return this.steps.length ? this.steps[0].seq : null;
    },
    select(seq) {
      this.selectedSeq = seq;
    },
    togglePhase(p) {
      const i = this.phases.indexOf(p);
      if (i >= 0) this.phases.splice(i, 1);
      else this.phases.push(p);
    },
    clearFilters() {
      this.onlyProblems = false;
      this.phases = [];
    },
    /** Unknown phase keeps its raw code and a neutral colour — never dressed up as a known one. */
    phaseMeta(phase) {
      const key = String(phase || '');
      return PHASE_META[key] || { color: 'blue-grey', title: 'phase ที่หน้าจอนี้ยังไม่รู้จัก: ' + (key || '(ว่าง)') };
    },
    /** Same rule for outcome: an unknown value shows itself, in grey, with an honest tooltip. */
    outcomeMeta(outcome) {
      const key = String(outcome || '');
      return OUTCOME_META[key] || {
        label: key || '—',
        color: 'blue-grey',
        variant: 'outlined',
        icon: 'mdi-help',
        tone: 'dim',
        alert: 'info',
        title: 'ผลลัพธ์ที่หน้าจอนี้ยังไม่รู้จัก: ' + (key || '(ว่าง)'),
      };
    },
    itemOpMeta(op) {
      const key = String(op || '');
      return ITEM_OP_META[key] || {
        label: key || '—',
        color: 'blue-grey',
        variant: 'outlined',
        title: 'op ของรายการย่อยที่หน้าจอนี้ยังไม่รู้จัก: ' + (key || '(ว่าง)'),
      };
    },
    fieldTh(field) {
      return FIELD_TH[String(field || '')] || '';
    },
    /** Thai gloss for a `kind` — `''` when this build has no entry (never an invented one). */
    kindTh(kind) {
      return KIND_TH[String(kind || '')] || '';
    },
    /** Tooltip for the raw `kind` on a list row: the gloss, or an honest "ยังไม่รู้จัก". */
    kindTitle(kind) {
      const th = this.kindTh(kind);
      return th ? String(kind) + ' — ' + th : 'ชนิดขั้นที่หน้าจอนี้ยังไม่รู้จัก: ' + (kind || '(ว่าง)');
    },
    /** Same rule as every other vocabulary here: an unknown op shows its own raw code. */
    opMeta(op) {
      const key = String(op || '');
      return STEP_OP_META[key] || {
        label: key || '—',
        color: 'blue-grey',
        variant: 'outlined',
        title: 'op ที่หน้าจอนี้ยังไม่รู้จัก: ' + (key || '(ว่าง)'),
      };
    },
    /**
     * Split a `reasonTh` into `{ code, codeTh, text }` — see `REASON_CODE_RE`.
     * A reason with no leading code comes back as `{ code: null, text: <the whole thing> }`,
     * which the template renders exactly the same minus the tag.
     */
    reasonParts(reason) {
      const raw = String(reason || '');
      if (!raw) return null;
      const m = REASON_CODE_RE.exec(raw);
      if (!m) return { code: null, codeTh: '', text: raw };
      return { code: m[1], codeTh: REASON_CODE_TH[m[1]] || m[1], text: m[2] };
    },
    /**
     * The "why" line of a list row — `null` when the row should not carry one.
     *
     * Shown when the step did NOT simply succeed, and ALSO whenever the row would otherwise
     * be blank below its chips (no `ref`, no `labelTh`) — which is precisely the shape the
     * folded already-answered skip has, and the shape that used to render as a row saying
     * nothing but "ข้าม".
     */
    whyOf(s) {
      if (!s || !s.reasonTh) return null;
      const bare = !s.ref && !s.labelTh;
      if (String(s.outcome || '') === 'applied' && !bare) return null;
      return this.reasonParts(s.reasonTh);
    },
    /** The one-line summary under a list row: what the step was aiming at. */
    refLine(s) {
      // 🔴 `op` is NO LONGER concatenated here — it has its own chip on the line above, and
      // printing it twice pushed the ref (the thing being read) off the visible line.
      const head = String((s && s.ref) || '');
      const label = String((s && s.labelTh) || '');
      if (head && label) return head + ' — ' + label;
      return head || label;
    },
    /** Every step a row draws — one for a plain row, the whole run for an OPEN group, none for a closed one. */
    rowSteps(r) {
      if (!r) return [];
      if (r.type !== 'group') return r.steps;
      return r.expanded ? r.steps : [];
    },
    rowFirstSeq(r) {
      return (r && r.steps.length) ? r.steps[0].seq : 0;
    },
    rowLastSeq(r) {
      return (r && r.steps.length) ? r.steps[r.steps.length - 1].seq : 0;
    },
    toggleGroup(firstSeq) {
      const i = this.expandedGroups.indexOf(firstSeq);
      if (i >= 0) this.expandedGroups.splice(i, 1);
      else this.expandedGroups.push(firstSeq);
    },
    /**
     * The header line of a folded run: WHAT was stepped over and WHY, tallied by reason code.
     * The refs come after the tally because "ข้ามเพราะอะไร" is the question; "ข้อไหนบ้าง" is
     * the follow-up, and the full list is one click away.
     */
    groupSummary(steps) {
      const order = [];
      const tally = {};
      const refs = [];
      (steps || []).forEach((s) => {
        const p = this.reasonParts(s && s.reasonTh);
        const code = (p && p.code) || REASON_CODE_OTHER;
        if (tally[code] == null) { tally[code] = 0; order.push(code); }
        tally[code] += 1;
        if (s && s.ref && refs.indexOf(s.ref) < 0) refs.push(s.ref);
      });
      const why = order.map((c) => (REASON_CODE_TH[c] || c) + ' ' + tally[c]).join(' · ');
      if (!refs.length) return why;
      const head = refs.slice(0, 3).join(', ');
      const rest = refs.length > 3 ? ' +' + (refs.length - 3) : '';
      return why + ' · ' + head + rest;
    },
    /**
     * How many steps sit between two rendered rows. Non-zero has TWO causes and the tooltip
     * says both: `Trim` dropped the middle of the turn, or the active filter hid them.
     * 🔴 Measured between the LAST seq of the previous row and the FIRST of this one — a
     * folded run spans many seqs, so comparing row-to-row on a single seq would report the
     * whole run as a gap.
     */
    gapBefore(i) {
      if (i <= 0) return 0;
      const prev = this.rows[i - 1];
      const cur = this.rows[i];
      const gap = Number(this.rowFirstSeq(cur)) - Number(this.rowLastSeq(prev)) - 1;
      return gap > 0 ? gap : 0;
    },
    /** `null` is a real answer (the column WAS null) and is not the same as an empty string. */
    showValue(v) {
      if (v === null || v === undefined) return 'NULL';
      if (v === '') return '(สตริงว่าง)';
      return String(v);
    },
    valueClass(v) {
      return (v === null || v === undefined || v === '') ? 'tf-null' : null;
    },
    onCopyStep() {
      this.$emit('copy', {
        text: JSON.stringify(this.selectedStep, null, 2),
        label: 'Flow step ' + ((this.selectedStep && this.selectedStep.seq) || ''),
      });
    },
    onCopyDetailJson() {
      this.$emit('copy', {
        text: String((this.selectedStep && this.selectedStep.detailJson) || ''),
        label: 'detailJson',
      });
    },
  },
};
</script>

<style scoped>
.tf-panel {
  min-width: 0;
}

.tf-sep {
  display: inline-block;
  width: 1px;
  height: 14px;
  background: rgba(var(--v-theme-on-surface), 0.18);
  margin: 0 2px;
}

/* master-detail — md+ side by side, xs/sm stacked (list on top).
   🔴 Each pane scrolls INSIDE itself so this panel can never give the page a second
   scrollbar, no matter how many steps a turn produced. */
.tf-split {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.tf-split--stacked {
  flex-direction: column;
  gap: 8px;
}

.tf-list {
  flex: 0 0 40%;
  max-width: 40%;
  min-width: 0;
  max-height: 52vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 4px;
}

.tf-split--stacked .tf-list {
  flex: none;
  width: 100%;
  max-width: 100%;
  max-height: 30vh;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding-bottom: 4px;
}

.tf-detail {
  flex: 1 1 auto;
  min-width: 0;
  max-height: 52vh;
  overflow-y: auto;
}

.tf-split--stacked .tf-detail {
  width: 100%;
  max-height: 48vh;
}

/* One step in the list. The left rule carries the outcome tone; the chip repeats it in
   words, so colour is never the only signal. */
.tf-step {
  border-left: 3px solid rgba(var(--v-theme-on-surface), 0.12);
  padding: 3px 4px 3px 8px;
  margin-bottom: 4px;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
}

.tf-step:hover {
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.tf-step--on {
  background: rgba(var(--v-theme-primary), 0.1);
  outline: 1px solid rgba(var(--v-theme-primary), 0.45);
}

.tf-step--ok {
  border-left-color: #43a047;
}

.tf-step--bad {
  border-left-color: #e53935;
}

.tf-step--dim {
  border-left-color: rgba(var(--v-theme-on-surface), 0.2);
}

.tf-seq {
  font-family: monospace;
  color: rgba(var(--v-theme-on-surface), 0.55);
  min-width: 22px;
}

.tf-kind {
  font-family: monospace;
  font-size: 11px;
  word-break: normal;
  overflow-wrap: anywhere;
}

/* 🔴 `overflow-wrap: anywhere`, NOT `word-break` — Thai offers the browser no break
   opportunity at all, while a field key must only break when it genuinely does not fit. */
.tf-step-sub {
  word-break: normal;
  overflow-wrap: anywhere;
}

/* 🔴 Clamped to two lines, with the full text on `title`. A `visibleWhen` expression can run
   to a couple of hundred characters, and letting one skip push the next four rows off screen
   is how the list stopped being scannable in the first place. */
.tf-step-why {
  margin-top: 1px;
  word-break: normal;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* The reason code, as a tag rather than prose — it is the thing the group tally counts. */
.tf-code-tag {
  display: inline-block;
  font-size: 10px;
  line-height: 1.5;
  padding: 0 4px;
  margin-right: 4px;
  border-radius: 3px;
  background: rgba(var(--v-theme-on-surface), 0.1);
  color: rgba(var(--v-theme-on-surface), 0.75);
  white-space: nowrap;
}

/* A folded run of walk.skip. Dashed, so it never reads as one more step — it is a lid. */
.tf-group {
  border: 1px dashed rgba(var(--v-theme-on-surface), 0.22);
  border-radius: 4px;
  padding: 3px 4px 3px 8px;
  margin-bottom: 4px;
  cursor: pointer;
  max-width: 100%;
  overflow: hidden;
}

.tf-group:hover {
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.tf-group--on {
  border-style: solid;
}

/* Held open by the selection, not by the reader — so it must not look clickable. */
.tf-group--pinned {
  cursor: default;
}

.tf-group--pinned:hover {
  background: none;
}

/* Steps of an open group sit under their lid rather than beside it. */
.tf-step--in-group {
  margin-left: 12px;
}

.tf-legend .tf-table td {
  vertical-align: top;
}

.tf-legend-code {
  width: 34%;
  white-space: nowrap;
}

.tf-gap {
  color: rgba(var(--v-theme-on-surface), 0.45);
  padding: 2px 0 2px 10px;
  font-family: monospace;
}

.tf-meta {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 4px;
}

.tf-meta-key {
  width: 34%;
  padding: 2px 6px 2px 0;
  font-family: monospace;
  color: rgba(var(--v-theme-on-surface), 0.6);
  vertical-align: top;
}

.tf-meta-val {
  padding: 2px 0;
  word-break: normal;
  overflow-wrap: anywhere;
}

.tf-labelth {
  font-size: 13px;
  margin-top: 4px;
  word-break: normal;
  overflow-wrap: anywhere;
}

.tf-sec-head {
  margin-top: 10px;
  margin-bottom: 2px;
  color: rgba(var(--v-theme-on-surface), 0.7);
}

/* A table wider than this column scrolls in its OWN box — never the page. */
.tf-scroll-x {
  overflow-x: auto;
  max-width: 100%;
}

.tf-table {
  width: 100%;
  min-width: 320px;
  border-collapse: collapse;
  font-size: 12px;
}

.tf-table th {
  text-align: left;
  font-weight: 600;
  padding: 3px 6px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.18);
  color: rgba(var(--v-theme-on-surface), 0.6);
  white-space: nowrap;
}

.tf-table td {
  padding: 3px 6px;
  vertical-align: top;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  word-break: normal;
  overflow-wrap: anywhere;
}

.tf-col-field {
  width: 30%;
}

.tf-col-val {
  width: 35%;
}

.tf-mono {
  font-family: monospace;
  word-break: normal;
  overflow-wrap: anywhere;
}

.tf-sub {
  font-size: 11px;
  color: rgba(var(--v-theme-on-surface), 0.55);
}

.tf-null {
  color: rgba(var(--v-theme-on-surface), 0.38);
  font-family: monospace;
}

.tf-arrow {
  color: rgba(var(--v-theme-on-surface), 0.45);
}

/* Same amber as `.cf-changed` in TaxPlaygroundConfigFormPanelV2 — one tone means
   "changed in this turn" across every panel of this console. */
.tf-changed {
  background: rgba(255, 193, 7, 0.22);
}

.tf-changed .tf-col-field,
.tf-changed td:first-child {
  border-left: 2px solid #ffb300;
}

/* 🔴 This panel declares its OWN dark box (`.tf-code`, not `.code-block`): scoped styles
   do not inherit, and `.code-block` is a name `TaxLogV2Detail.vue` reaches into with
   `:deep()`. Its own `max-height` keeps a big blob inside the pane. */
.tf-code {
  font-family: monospace;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: normal;
  overflow-wrap: anywhere;
  background: #0f1115;
  color: #dbe2ea;
  border-radius: 6px;
  padding: 8px;
  max-height: 30vh;
  overflow: auto;
}
</style>
